import axios from "axios";

const CHUNK = 2 * 1024 * 1024;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const rid = () => Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_-"[b % 64]).join("");
const retryable = (ex) => !ex?.response || ex.code === "ECONNABORTED" || ex.response.status >= 500 || ex.response.status === 429 || ex.response.status === 408;

async function postRetry(url, fd, onProgress, config = {}) {
  let last;
  for (let attempt = 0; attempt < 4; attempt++) {
    try { return await axios.post(url, fd, { timeout: 90000, onUploadProgress: onProgress, ...config }); }
    catch (ex) { last = ex; if (!retryable(ex)) throw ex; await sleep(800 * 2 ** attempt); }
  }
  throw last;
}

export async function uploadChunked({ url, file, fields = {}, onProgress, chunkSize = CHUNK, headers }) {
  const total = Math.max(1, Math.ceil(file.size / chunkSize));
  const uploadId = rid();
  let last;
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => v != null && fd.append(k, v));
    fd.append("upload_id", uploadId); fd.append("index", String(i)); fd.append("total", String(total));
    fd.append("filename", file.name); fd.append("content_type", file.type || "");
    fd.append("chunk", file.slice(i * chunkSize, (i + 1) * chunkSize), file.name);
    last = await postRetry(url, fd, (e) => onProgress?.((i + (e.total ? e.loaded / e.total : 0)) / total), { withCredentials: true, headers });
  }
  onProgress?.(1);
  return last;
}

export async function uploadIntakeFile({ api, token, jobId, uploadToken, file, onProgress }) {
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  const uploadId = rid();
  const base = `${api}/api/intake/${token}/jobs/${jobId}/documents`;
  if (total === 1) {
    const fd = new FormData(); fd.append("upload_token", uploadToken); fd.append("file", file);
    return postRetry(base, fd, (e) => onProgress?.(e.total ? e.loaded / e.total : 0));
  }
  for (let i = 0; i < total; i++) {
    const fd = new FormData();
    fd.append("upload_token", uploadToken); fd.append("upload_id", uploadId); fd.append("index", String(i)); fd.append("total", String(total));
    fd.append("filename", file.name); fd.append("content_type", file.type || "");
    fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    await postRetry(`${base}/chunk`, fd, (e) => onProgress?.((i + (e.total ? e.loaded / e.total : 0)) / total));
  }
  onProgress?.(1);
}

export async function uploadAll(files, opts, onState, concurrency = 2) {
  const state = files.map((f) => ({ name: f.name, pct: 0, status: "queued", error: "" }));
  const emit = () => onState([...state]);
  emit();
  let next = 0;
  const worker = async () => {
    while (next < files.length) {
      const i = next++;
      state[i].status = "uploading"; emit();
      try {
        await uploadIntakeFile({ ...opts, file: files[i], onProgress: (p) => { state[i].pct = Math.round(p * 100); emit(); } });
        state[i].status = "done"; state[i].pct = 100;
      } catch (ex) {
        state[i].status = "failed";
        state[i].error = ex?.response?.data?.detail || (ex?.response?.status === 413 ? "file too large (max 10MB)" : ex?.code === "ECONNABORTED" ? "connection timed out" : "upload failed");
      }
      emit();
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, files.length) }, worker));
  return state;
}
